import React, { useEffect, useMemo, useState } from "react";
import { format, addDays, startOfDay, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { Calendar as CalendarIcon, X } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const TIME_SLOTS = ["19:30", "20:00", "20:30", "21:00", "21:30", "22:00"];
const JS_CLOSED_DAYS = new Set([0, 1, 2]);

export default function ReservationEditor({ open, onOpenChange, reservation, onSaved }) {
  const isEdit = Boolean(reservation);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [partySize, setPartySize] = useState("2");
  const [notes, setNotes] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [force, setForce] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const minDate = useMemo(() => startOfDay(new Date()), []);
  const maxDate = useMemo(() => addDays(new Date(), 180), []);

  useEffect(() => {
    if (!open) return;
    if (reservation) {
      setName(reservation.name || "");
      setPhone(reservation.phone || "");
      setDate(reservation.date ? parseISO(reservation.date) : null);
      setTime(reservation.time || "");
      setPartySize(String(reservation.party_size || 2));
      setNotes(reservation.notes || "");
      setAdminNotes(reservation.admin_notes || "");
    } else {
      setName(""); setPhone(""); setDate(null); setTime("");
      setPartySize("2"); setNotes(""); setAdminNotes(""); setForce(false);
    }
  }, [open, reservation]);

  const canSubmit = name.trim().length >= 2 && phone.trim().length >= 6 && date && time;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { toast.error("Preencha nome, telemóvel, data e hora."); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        phone: phone.trim(),
        date: format(date, "yyyy-MM-dd"),
        time,
        party_size: Number(partySize),
        notes: notes.trim(),
        admin_notes: adminNotes.trim(),
      };
      let data;
      if (isEdit) {
        const r = await api.patch(`/reservations/${reservation.id}`, payload);
        data = r.data;
        toast.success("Reserva atualizada.");
      } else {
        const r = await api.post("/reservations/admin", { ...payload, force });
        data = r.data;
        toast.success("Reserva criada e confirmada.");
      }
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erro ao guardar.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-[#F4EFEB] w-[95vw] max-w-2xl rounded-none flex flex-col max-h-[85dvh] p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/5 shrink-0">
          <DialogTitle className="font-display text-3xl text-[#F4EFEB] pr-8">
            {isEdit ? "Editar Reserva" : "Nova Reserva"}
          </DialogTitle>
          <DialogDescription className="text-[#A19D98] text-sm">
            {isEdit
              ? "Atualize os dados da reserva. Alterações são sincronizadas automaticamente com o Google Sheets."
              : "Crie uma reserva interna (já confirmada). Não é enviado SMS ao cliente."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex-1 overflow-y-auto overscroll-contain px-6 py-5 space-y-5" data-testid="reservation-editor-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#F4EFEB]">Nome</Label>
              <Input data-testid="editor-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-2 lm-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#F4EFEB]">Telemóvel</Label>
              <Input data-testid="editor-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-2 lm-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#F4EFEB]">Data</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="editor-date" className="mt-2 lm-input text-left flex items-center gap-2">
                    <CalendarIcon className="h-3.5 w-3.5 text-[#A19D98]" />
                    {date ? format(date, "d MMM yyyy", { locale: pt }) : <span className="text-[#6b6763]">Escolher</span>}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#0E0E0E] border-white/10 z-[100]" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < minDate || d > maxDate || JS_CLOSED_DAYS.has(d.getDay())}
                    locale={pt}
                    initialFocus
                    className="bg-[#0E0E0E] text-[#F4EFEB]"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#F4EFEB]">Hora</Label>
              <Select value={time} onValueChange={setTime}>
                <SelectTrigger data-testid="editor-time" className="mt-2 lm-input">
                  <SelectValue placeholder="Escolher" />
                </SelectTrigger>
                <SelectContent className="bg-[#0E0E0E] border-white/10 text-[#F4EFEB] z-[100]">
                  {TIME_SLOTS.map((t) => (
                    <SelectItem key={t} value={t} className="focus:bg-[#1A1A1A] focus:text-[#D45A2C]">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#F4EFEB]">Pessoas</Label>
              <Select value={partySize} onValueChange={setPartySize}>
                <SelectTrigger data-testid="editor-party" className="mt-2 lm-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0E0E0E] border-white/10 text-[#F4EFEB] z-[100]">
                  {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                    <SelectItem key={n} value={String(n)} className="focus:bg-[#1A1A1A] focus:text-[#D45A2C]">{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#F4EFEB]">Notas do cliente</Label>
            <Textarea data-testid="editor-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-2 lm-input resize-none" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#F4EFEB]">
              Notas internas <span className="normal-case tracking-normal text-[#6b6763]">(não vão para o cliente)</span>
            </Label>
            <Textarea data-testid="editor-admin-notes" value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={2} className="mt-2 lm-input resize-none" />
          </div>

          {!isEdit && (
            <label className="flex items-center gap-2 text-xs text-[#A19D98] cursor-pointer">
              <Checkbox checked={force} onCheckedChange={(v) => setForce(Boolean(v))} className="border-white/30 data-[state=checked]:bg-[#D45A2C] data-[state=checked]:border-[#D45A2C]" data-testid="editor-force" />
              Forçar (ignorar limite de capacidade)
            </label>
          )}

          <div className="flex gap-3 justify-end pt-4 border-t border-white/5 sticky bottom-0 bg-[#0A0A0A] -mx-6 px-6 -mb-5 py-4">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none text-[#A19D98] hover:text-[#F4EFEB] hover:bg-transparent">
              <X className="h-4 w-4 mr-1" /> Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit || submitting} data-testid="editor-submit"
              className="bg-[#D45A2C] hover:bg-[#B84B23] text-white rounded-none px-6 text-xs uppercase tracking-[0.25em]">
              {submitting ? "A guardar..." : isEdit ? "Guardar" : "Criar reserva"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
